/**
 * BoardView — the "Voir tout" surface (v0.12).
 *
 * Renders the project's reports in a master/detail layout with a KPI
 * strip on top and a filter row below it. Scope follows the project's
 * `share_reports_with_widget` flag server-side; the client just renders
 * what the endpoint returns and uses `is_mine` to permission-gate the
 * detail-pane status buttons.
 *
 * Architecture notes:
 *   - The list polls every 30s while open (same cadence as MineList).
 *     Filters trigger an immediate refetch.
 *   - The KPI strip refetches alongside the list so the counts always
 *     match what the user sees.
 *   - Detail pane is the existing ReportDetailView used by My-reports +
 *     This-week. Permission-gating happens via the `canModerate` prop
 *     (false for project-scoped rows that aren't `is_mine`).
 *   - The whole tab assumes the Modal it's rendered into has been
 *     `expanded` — see Modal.tsx + .modal.is-expanded in styles.ts.
 */

import { h, type JSX } from 'preact'
import { useEffect, useMemo, useState } from 'preact/hooks'

import type { ApiClient, BoardListPage } from '../api/client'
import type {
  BoardFilters,
  FeedbackSeverity,
  FeedbackType,
  ReportStatus,
  WidgetBoardKpis,
  WidgetBoardRow,
} from '../types'
import type { StringKey } from './i18n'
import { ReportDetailView } from './ReportDetailView'

const POLL_MS = 30_000

const STATUSES: ReportStatus[] = [
  'new',
  'in_progress',
  'awaiting_validation',
  'closed',
  'rejected',
]
const TYPES: FeedbackType[] = ['bug', 'feature', 'question', 'praise', 'typo']
const SEVERITIES: FeedbackSeverity[] = ['blocker', 'high', 'medium', 'low']

interface BoardViewProps {
  api: ApiClient
  externalId: string
  strings: Record<StringKey, string>
}

export function BoardView({ api, externalId, strings }: BoardViewProps) {
  const [filters, setFilters] = useState<BoardFilters>({})
  const [page, setPage] = useState<BoardListPage | null>(null)
  const [kpis, setKpis] = useState<WidgetBoardKpis | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  // Detail-pane mount tick — increments when the user picks a row so the
  // panel can re-mount and re-fetch without us caching old data.
  const [detailKey, setDetailKey] = useState(0)

  const activeFilterCount = useMemo(() => {
    return (
      (filters.status?.length ?? 0) +
      (filters.type?.length ?? 0) +
      (filters.severity?.length ?? 0) +
      (filters.q ? 1 : 0) +
      (filters.mine ? 1 : 0)
    )
  }, [filters])

  // Single source of truth for list + KPIs — they refetch together so the
  // counts never drift from the visible rows.
  useEffect(() => {
    let cancelled = false
    let timer: ReturnType<typeof setTimeout> | null = null
    const tick = async () => {
      try {
        const [list, k] = await Promise.all([
          api.listBoard(externalId, filters),
          api.fetchBoardKpis(externalId, filters),
        ])
        if (cancelled) return
        setPage(list)
        setKpis(k)
        setError(null)
      } catch (e) {
        if (cancelled) return
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        if (!cancelled) setLoading(false)
        if (!cancelled) timer = setTimeout(tick, POLL_MS)
      }
    }
    setLoading(true)
    void tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [api, externalId, filtersHash(filters)])

  const selectedRow = useMemo(() => {
    if (!selectedId || !page) return null
    return page.results.find((r) => r.id === selectedId) ?? null
  }, [selectedId, page])

  const onPickRow = (row: WidgetBoardRow) => {
    setSelectedId(row.id)
    setDetailKey((k) => k + 1)
  }

  return (
    <div class="board-view">
      <BoardHeader strings={strings} kpis={kpis} />
      <BoardFilters
        filters={filters}
        onChange={setFilters}
        activeCount={activeFilterCount}
        strings={strings}
      />
      <div class="board-body">
        <div class="board-list-wrap" aria-busy={loading && !page}>
          {error && <div class="board-error">{strings['board.list.error']}</div>}
          {!error && loading && !page && (
            <BoardListSkeleton />
          )}
          {!error && page && page.results.length === 0 && !loading && (
            <BoardEmpty strings={strings} />
          )}
          {!error && page && page.results.length > 0 && (
            <BoardList
              rows={page.results}
              selectedId={selectedId}
              onPick={onPickRow}
              strings={strings}
              total={page.count}
            />
          )}
        </div>
        <div class={`board-detail-wrap ${selectedRow ? 'has-selection' : ''}`}>
          {selectedRow ? (
            <ReportDetailView
              key={detailKey}
              api={api}
              externalId={externalId}
              reportId={selectedRow.id}
              strings={strings}
              onBack={() => setSelectedId(null)}
              canModerate={selectedRow.is_mine}
              variant="board"
            />
          ) : (
            <div class="board-detail-empty">
              <DetailEmptyIllustration />
              <p>{strings['board.detail.empty']}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ---------- subcomponents -------------------------------------------------

function BoardHeader({
  strings,
  kpis,
}: {
  strings: Record<StringKey, string>
  kpis: WidgetBoardKpis | null
}) {
  const scopeLabel =
    kpis?.scope === 'project'
      ? strings['board.scope.project']
      : strings['board.scope.mine']
  return (
    <header class="board-header">
      <div class="board-header-title">
        <span class="board-header-emoji" aria-hidden="true">📋</span>
        <div>
          <h2 class="board-header-h">{strings['tab.board']}</h2>
          <p class="board-header-sub">
            {kpis ? `${kpis.total} · ${scopeLabel}` : scopeLabel}
          </p>
        </div>
      </div>
      <BoardKpiStrip kpis={kpis} strings={strings} />
    </header>
  )
}

function BoardKpiStrip({
  kpis,
  strings,
}: {
  kpis: WidgetBoardKpis | null
  strings: Record<StringKey, string>
}) {
  // Always render 4 cards so the strip's height is stable while the
  // first fetch is in flight — eliminates the layout shift the PNR
  // page has on its first paint.
  const cells: { key: string; label: string; value: string; tone: string }[] = [
    {
      key: 'new',
      label: strings['board.kpi.new'],
      value: String(kpis?.by_status?.new ?? 0),
      tone: 'tone-new',
    },
    {
      key: 'in_progress',
      label: strings['board.kpi.in_progress'],
      value: String(kpis?.by_status?.in_progress ?? 0),
      tone: 'tone-progress',
    },
    {
      key: 'awaiting_validation',
      label: strings['board.kpi.awaiting_validation'],
      value: String(kpis?.by_status?.awaiting_validation ?? 0),
      tone: 'tone-validation',
    },
    {
      key: 'rate',
      label: strings['board.kpi.resolution_rate'],
      value: kpis ? `${Math.round((kpis.resolution_rate || 0) * 100)}%` : '—',
      tone: 'tone-rate',
    },
  ]
  return (
    <div class={`board-kpi-strip ${kpis ? 'is-ready' : 'is-loading'}`} aria-live="polite">
      {cells.map((c) => (
        <div key={c.key} class={`board-kpi-card ${c.tone}`}>
          <div class="board-kpi-value">{c.value}</div>
          <div class="board-kpi-label">{c.label}</div>
        </div>
      ))}
    </div>
  )
}

function BoardFilters({
  filters,
  onChange,
  activeCount,
  strings,
}: {
  filters: BoardFilters
  onChange: (f: BoardFilters) => void
  activeCount: number
  strings: Record<StringKey, string>
}) {
  // Under `exactOptionalPropertyTypes: true` we can't set keys to
  // `undefined` to clear them — the optional slot doesn't accept the
  // explicit undefined. Drop the key via destructuring instead.
  const setStatus = (value: ReportStatus | '') => {
    if (value === '') {
      const { status: _drop, ...rest } = filters
      void _drop
      onChange(rest)
    } else {
      onChange({ ...filters, status: [value] })
    }
  }
  const setType = (value: FeedbackType | '') => {
    if (value === '') {
      const { type: _drop, ...rest } = filters
      void _drop
      onChange(rest)
    } else {
      onChange({ ...filters, type: [value] })
    }
  }
  const setSeverity = (value: FeedbackSeverity | '') => {
    if (value === '') {
      const { severity: _drop, ...rest } = filters
      void _drop
      onChange(rest)
    } else {
      onChange({ ...filters, severity: [value] })
    }
  }
  const setSearch = (q: string) => onChange({ ...filters, q })
  const toggleMine = () => {
    if (filters.mine) {
      const { mine: _drop, ...rest } = filters
      void _drop
      onChange(rest)
    } else {
      onChange({ ...filters, mine: true })
    }
  }
  const clear = () => onChange({})
  return (
    <div class="board-filters">
      <select
        class="board-filter-select"
        aria-label={strings['board.filter.status']}
        value={filters.status?.[0] ?? ''}
        onChange={(e) =>
          setStatus((e.target as HTMLSelectElement).value as ReportStatus | '')
        }
      >
        <option value="">{strings['board.filter.status']}</option>
        {STATUSES.map((s) => (
          <option key={s} value={s}>
            {strings[`board.kpi.${s}` as StringKey] ?? s}
          </option>
        ))}
      </select>
      <select
        class="board-filter-select"
        aria-label={strings['board.filter.type']}
        value={filters.type?.[0] ?? ''}
        onChange={(e) =>
          setType((e.target as HTMLSelectElement).value as FeedbackType | '')
        }
      >
        <option value="">{strings['board.filter.type']}</option>
        {TYPES.map((t) => (
          <option key={t} value={t}>
            {strings[`type.${t}` as StringKey] ?? t}
          </option>
        ))}
      </select>
      <select
        class="board-filter-select"
        aria-label={strings['board.filter.severity']}
        value={filters.severity?.[0] ?? ''}
        onChange={(e) =>
          setSeverity((e.target as HTMLSelectElement).value as FeedbackSeverity | '')
        }
      >
        <option value="">{strings['board.filter.severity']}</option>
        {SEVERITIES.map((s) => (
          <option key={s} value={s}>
            {strings[`severity.${s}` as StringKey] ?? s}
          </option>
        ))}
      </select>
      <input
        type="search"
        class="board-filter-search"
        placeholder={strings['board.filter.search.placeholder']}
        value={filters.q ?? ''}
        onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
      />
      <label class="board-filter-toggle">
        <input
          type="checkbox"
          checked={Boolean(filters.mine)}
          onChange={toggleMine}
        />
        <span>{strings['board.filter.mine']}</span>
      </label>
      {activeCount > 0 && (
        <button type="button" class="board-filter-clear" onClick={clear}>
          <CloseIcon />
          {strings['board.filter.clear']}
        </button>
      )}
    </div>
  )
}

function BoardList({
  rows,
  selectedId,
  onPick,
  strings,
  total,
}: {
  rows: WidgetBoardRow[]
  selectedId: string | null
  onPick: (row: WidgetBoardRow) => void
  strings: Record<StringKey, string>
  total: number
}) {
  return (
    <>
      <div class="board-list-count">
        {strings['board.list.count']
          .replace('{n}', String(rows.length))
          .replace('{total}', String(total))}
      </div>
      <ul class="board-list" role="list">
        {rows.map((row) => (
          <BoardRowCard
            key={row.id}
            row={row}
            selected={row.id === selectedId}
            onPick={onPick}
            strings={strings}
          />
        ))}
      </ul>
    </>
  )
}

function BoardRowCard({
  row,
  selected,
  onPick,
  strings,
}: {
  row: WidgetBoardRow
  selected: boolean
  onPick: (row: WidgetBoardRow) => void
  strings: Record<StringKey, string>
}) {
  const author = row.is_mine
    ? strings['board.list.you']
    : row.author_label || '—'
  return (
    <li>
      <button
        type="button"
        class={`board-row ${selected ? 'is-selected' : ''}`}
        onClick={() => onPick(row)}
        aria-pressed={selected}
      >
        <div class="board-row-badges">
          <span class={`badge status-${row.status}`}>
            {strings[`status.${row.status}` as StringKey] ?? row.status}
          </span>
          <span class={`badge severity-${row.severity}`}>
            {strings[`severity.${row.severity}` as StringKey] ?? row.severity}
          </span>
        </div>
        <div class="board-row-description">{row.description}</div>
        <div class="board-row-meta">
          <span class="board-row-author">{author}</span>
          {row.comment_count > 0 && (
            <span class="board-row-comments">
              <CommentIcon /> {row.comment_count}
            </span>
          )}
          <span class="board-row-time">{formatRelative(row.created_at)}</span>
        </div>
      </button>
    </li>
  )
}

function BoardEmpty({ strings }: { strings: Record<StringKey, string> }) {
  return (
    <div class="board-empty">
      <EmptyIllustration />
      <h3>{strings['board.list.empty.title']}</h3>
      <p>{strings['board.list.empty.description']}</p>
    </div>
  )
}

function BoardListSkeleton() {
  return (
    <ul class="board-list board-list-skeleton" aria-hidden="true">
      {Array.from({ length: 5 }).map((_, i) => (
        <li key={i}>
          <div class="board-row skeleton-row">
            <div class="skeleton-line skeleton-badges" />
            <div class="skeleton-line skeleton-text" />
            <div class="skeleton-line skeleton-text short" />
          </div>
        </li>
      ))}
    </ul>
  )
}

// ---------- icons ---------------------------------------------------------

function CommentIcon(): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  )
}

function CloseIcon(): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  )
}

function EmptyIllustration(): JSX.Element {
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" fill="none" aria-hidden="true">
      <circle cx="32" cy="32" r="28" stroke="currentColor" stroke-opacity="0.18" stroke-width="2" />
      <path d="M22 32h20M32 22v20" stroke="currentColor" stroke-opacity="0.35" stroke-width="2" stroke-linecap="round" />
    </svg>
  )
}

function DetailEmptyIllustration(): JSX.Element {
  return (
    <svg width="80" height="80" viewBox="0 0 64 64" fill="none" aria-hidden="true">
      <rect x="10" y="12" width="44" height="36" rx="4" stroke="currentColor" stroke-opacity="0.22" stroke-width="2" />
      <line x1="18" y1="22" x2="46" y2="22" stroke="currentColor" stroke-opacity="0.22" stroke-width="2" stroke-linecap="round" />
      <line x1="18" y1="30" x2="38" y2="30" stroke="currentColor" stroke-opacity="0.18" stroke-width="2" stroke-linecap="round" />
      <line x1="18" y1="38" x2="32" y2="38" stroke="currentColor" stroke-opacity="0.14" stroke-width="2" stroke-linecap="round" />
    </svg>
  )
}

// ---------- helpers -------------------------------------------------------

function filtersHash(f: BoardFilters): string {
  // Stable serialization for the useEffect dep array — avoids re-running
  // when the BoardView re-renders with a new object identity but the
  // same filter values.
  return JSON.stringify({
    s: f.status?.slice().sort(),
    t: f.type?.slice().sort(),
    sv: f.severity?.slice().sort(),
    q: f.q ?? '',
    m: Boolean(f.mine),
  })
}

function formatRelative(iso: string): string {
  // Tiny "5m / 3h / 2d / 5w" formatter — same shape PNR uses. Doesn't
  // depend on date-fns; keeps the bundle lean.
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return ''
  const delta = Math.max(0, Date.now() - then)
  const m = Math.floor(delta / 60_000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h`
  const d = Math.floor(h / 24)
  if (d < 7) return `${d}d`
  const w = Math.floor(d / 7)
  return `${w}w`
}
